import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export const Modal = ({ open, onClose, title, children, maxWidth = "max-w-2xl" }) => {
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      const f = panelRef.current?.querySelector(
        'button,a[href],input,textarea,select,[tabindex]:not([tabindex="-1"])'
      );
      f?.focus();
    }, 40);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
      if (triggerRef.current && triggerRef.current.focus) triggerRef.current.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const f = panelRef.current.querySelectorAll(
          'a[href],button:not([disabled]),input,textarea,select,[tabindex]:not([tabindex="-1"])'
        );
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="absolute inset-0 bg-legacy/70 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={panelRef}
        className={`relative ${maxWidth} w-full max-h-[88vh] overflow-y-auto bg-ivory rounded-card shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] border-t-2 border-gold`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close dialog"
          data-testid="modal-close"
          className="sticky top-0 float-right z-10 m-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-legacy text-ivory hover:bg-bronze transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
        >
          <X size={18} />
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
};
